import { ITEM_KINDS } from "../../../api/content/reviewDecoders";
import {
  JsonContractError,
  readArray,
  readInteger,
  readNullableString,
  readObject,
  readOneOf,
  readOptionalString,
  readString,
  type JsonObject,
} from "../../../api/http/jsonDecoding";
import { parseCollectionVersionRef } from "../../../shared/references/contentReferences";
import { formatThreadEntryRef } from "../../../shared/references/threadReferences";
import { appPaths, PREPARATION_INTENT_PARAMETER, PREPARATION_REQUEST_PARAMETER } from "../../../shared/routing/appPaths";
import type { AppPath } from "../../../shell/workspace";
import type { EditWay, EntryPassageRef, RunEntries, ThreadIntent, ThreadIntentKind, ThreadSubject } from "./threadIntent";

/**
 * The preparation view's address (WA-11): `/threads/new?intent=<JSON>&request=<key>`. The
 * intent travels in the address because the view may open in the other pane, another window
 * (WA-10's frame), and so that Back and reload show the same preparation. `request` is the
 * idempotency key of its Start: made once when `prepare` is called, so a second Start after
 * Back or reload returns the Thread the first one made, never a second one.
 */
export interface PreparationAddress {
  readonly intent: ThreadIntent;
  readonly requestKey: string;
}

const INTENT_KINDS = ["run", "author", "edit", "import", "batch", "fork", "memory", "release"] as const satisfies readonly ThreadIntentKind[];

export function preparationPath(address: PreparationAddress): AppPath {
  return appPaths.threadPreparation(JSON.stringify(address.intent), address.requestKey);
}

/** The preparation an address names, or `null` when the address holds no valid intent. */
export function readPreparationAddress(search: URLSearchParams): PreparationAddress | null {
  const intentText = search.get(PREPARATION_INTENT_PARAMETER);
  const requestKey = search.get(PREPARATION_REQUEST_PARAMETER);
  if (intentText === null || requestKey === null || requestKey === "") {
    return null;
  }
  try {
    return { intent: readIntent(JSON.parse(intentText) as unknown), requestKey };
  } catch {
    return null;
  }
}

function readIntent(value: unknown): ThreadIntent {
  const intent = readObject(value, "Thread intent");
  const base = { projectId: readString(intent, "projectId"), returnTo: readReturnTo(intent) };
  const kind = readOneOf(intent, "kind", INTENT_KINDS);
  switch (kind) {
    case "run": {
      const contentVersion = readOptionalString(intent, "contentVersion");
      return {
        ...base,
        kind,
        workflow: readNullableString(intent, "workflow"),
        ...readRunEntries(intent),
        ...(contentVersion === undefined ? {} : { contentVersion }),
      };
    }
    case "author":
      return { ...base, kind, itemKind: readOneOf(intent, "itemKind", ITEM_KINDS), draft: intent["draft"] ?? null, name: readString(intent, "name") };
    case "edit": {
      const carry = readOptionalString(intent, "carry");
      const within = readOptionalString(intent, "within");
      return {
        ...base,
        kind,
        itemKind: readOneOf(intent, "itemKind", ITEM_KINDS),
        item: readString(intent, "item"),
        name: readString(intent, "name"),
        ...editWayOf(intent),
        ...(carry === undefined ? {} : { carry }),
        ...(within === undefined ? {} : { within }),
      };
    }
    case "import":
      return { ...base, kind, corpusId: readNullableString(intent, "corpusId") };
    case "batch":
      return { ...base, kind, fromThreadId: readString(intent, "fromThreadId") };
    case "fork":
      return { ...base, kind, threadId: readString(intent, "threadId"), atEntry: readInteger(intent, "atEntry") };
    case "memory": {
      const threadId = readString(intent, "threadId");
      return { ...base, kind, threadId, passages: readArray(intent["passages"], "passages", (passage) => readPassage(passage, threadId)), skipped: readInteger(intent, "skipped") };
    }
    case "release":
      return { ...base, kind };
  }
}

/** Only a path of this app: never another host or a scheme (WA-10). */
function readReturnTo(intent: JsonObject): AppPath | null {
  const returnTo = readNullableString(intent, "returnTo");
  return returnTo !== null && returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : null;
}

/** Entries come with the corpus version that holds them, or there are none (BA16-07). */
function readRunEntries(intent: JsonObject): RunEntries {
  const [first, ...rest] = readArray(intent["entries"], "entries", readSubject);
  if (first === undefined) return { entries: [], corpusVersion: null };
  const corpusVersion = readString(intent, "corpusVersion");
  if (parseCollectionVersionRef(corpusVersion) === null) throw new JsonContractError("Expected the entries' corpus version as `<collection id>@<commit>`.");
  return { entries: [first, ...rest], corpusVersion };
}

function readSubject(value: unknown): ThreadSubject {
  const subject = readObject(value, "subject");
  const document = readObject(subject["document"], "subject document");
  const segmentPath = readOptionalString(subject, "segmentPath");
  const passage = readOptionalString(subject, "passage");
  return {
    document: { documentId: readString(document, "documentId"), version: readString(document, "version") },
    label: readString(subject, "label"),
    ...(segmentPath === undefined ? {} : { segmentPath }),
    ...(passage === undefined ? {} : { passage }),
  };
}

/**
 * One passage of a memory intent. An address saved before passages carried their exact entry
 * (an open work tab, BA16-01) names only the number: its entry is read in `threadId`, the
 * Thread the passages were kept from, as that version cited it.
 */
function readPassage(value: unknown, threadId: string): EntryPassageRef {
  const passage = readObject(value, "passage");
  const entry = passage["entry"] === undefined ? formatThreadEntryRef({ threadId, number: readInteger(passage, "entryNumber") }) : readString(passage, "entry");
  return { entry, quote: readString(passage, "quote"), occurrence: readInteger(passage, "occurrence") };
}

/** The edit's way; an address written before ways had names said `byHand: true`. */
function editWayOf(intent: Readonly<Record<string, unknown>>): { readonly way?: EditWay } {
  const way = intent["way"];
  if (way === "by_hand" || way === "with_the_agent") return { way };
  return intent["byHand"] === true ? { way: "by_hand" } : {};
}
